import { Controller } from "@hotwired/stimulus";
import { Dropzone } from "dropzone";
import { DirectUpload } from "@rails/activestorage";
import * as tus from "tus-js-client";
import { refreshCsrfToken } from "../helpers/csrf";
import pdfBadgeSvg from "../../assets/images/icons/pdf-badge.svg?raw";

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1))}${sizes[i]}`;
};

const truncateFilename = (filename, maxLength = 15) => {
  if (filename.length <= maxLength) return filename;
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1) return filename.substring(0, maxLength - 3) + "...";
  const ext = filename.substring(lastDot);
  const available = maxLength - ext.length - 3;
  return filename.substring(0, available) + "..." + ext;
};

export default class extends Controller {
  static targets = [
    "addButton",
    "previews",
    "dropdown",
    "backdrop",
    "cameraInput",
    "filePickerInput",
    "documentPickerInput",
  ];
  static values = {
    url: String,
    videoInitUrl: String,
    paramName: { type: String, default: "comment[attachments][]" },
    maxFiles: { type: Number, default: 5 },
    maxFilesize: { type: Number, default: 20 },
    maxVideoFilesize: { type: Number, default: 100 },
    videoUploadingText: { type: String, default: "Uploading file" },
    acceptedFiles: String,
    existingAttachments: { type: Array, default: [] },
    existingVideos: { type: Array, default: [] },
    dictFileTooBig: {
      type: String,
      default: "File too large (max. {{maxFilesize}} MB)",
    },
    dictMaxFilesExceeded: {
      type: String,
      default: "Maximum {{maxFiles}} files allowed",
    },
    dictRemoveFile: { type: String, default: "Remove" },
    uploadError: { type: String, default: "Upload failed. Please try again." },
  };

  connect() {
    this.uploadsInProgress = 0;
    this.tusUploads = new Map();
    this.dropzone = this.#createDropzone();
    this.dropzone.enqueueFile = (file) => this.#upload(file);
    this.#loadExistingAttachments();
    this.#loadExistingVideos();

    this.dropzone.on("removedfile", (file) => this.#handleRemoval(file));
    this.dropzone.on("maxfilesexceeded", (file) =>
      this.#handleMaxExceeded(file),
    );
    this.dropzone.on("addedfile", (file) => {
      if (this.#isVideo(file)) {
        this.#renderVideoPreview(file);
      } else if (this.#isPdf(file)) {
        this.#renderPdfPreview(file);
      }
      this.#emitChange();
    });

    this.boundPreventSubmit = this.#preventSubmitDuringUpload.bind(this);
    this.element.addEventListener("submit", this.boundPreventSubmit);

    this.boundKeyDown = this.#handleKeyDown.bind(this);
    document.addEventListener("keydown", this.boundKeyDown);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.boundPreventSubmit);
    document.removeEventListener("keydown", this.boundKeyDown);
    this.tusUploads.forEach((upload) => upload.abort());
    this.tusUploads.clear();
    this.dropzone?.destroy();
  }

  toggleDropdown() {
    document.activeElement?.blur();
    if (this.hasDropdownTarget) {
      const isHidden = this.dropdownTarget.classList.toggle("hidden");
      if (this.hasBackdropTarget) {
        this.backdropTarget.classList.toggle("hidden", isHidden);
      }
    }
  }

  openFileDialog() {
    this.closeDropdown();
    this.dropzone.hiddenFileInput?.click();
  }

  openFilePicker() {
    this.closeDropdown();
    if (this.hasFilePickerInputTarget) {
      this.filePickerInputTarget.click();
    }
  }

  openDocumentPicker() {
    this.closeDropdown();
    if (this.hasDocumentPickerInputTarget) {
      this.documentPickerInputTarget.click();
    }
  }

  handleFilePick(event) {
    Array.from(event.target.files).forEach((file) => this.dropzone.addFile(file));
    event.target.value = "";
  }

  openCamera() {
    this.closeDropdown();
    if (this.hasCameraInputTarget) {
      this.cameraInputTarget.click();
    }
  }

  handleCameraCapture(event) {
    Array.from(event.target.files).forEach((file) => this.dropzone.addFile(file));
    event.target.value = "";
  }

  closeDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden");
    }
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden");
    }
  }

  #handleKeyDown(event) {
    if (event.key === "Escape") {
      this.closeDropdown();
    }
  }

  #isVideo(file) {
    return file.type?.startsWith("video/");
  }

  #isPdf(file) {
    return file.type === "application/pdf";
  }

  #createDropzone() {
    return new Dropzone(this.element, {
      url: this.urlValue,
      clickable: false,
      previewsContainer: this.previewsTarget,
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFilesizeValue,
      acceptedFiles: this.acceptedFilesValue,
      addRemoveLinks: false,
      uploadMultiple: false,
      createImageThumbnails: true,
      thumbnailWidth: 112,
      thumbnailHeight: 112,
      previewTemplate: this.#previewTemplate(),
      dictFileTooBig: this.dictFileTooBigValue,
      dictMaxFilesExceeded: this.dictMaxFilesExceededValue,
      accept: (file, done) => {
        if (this.dropzone.files.length > this.maxFilesValue) {
          this.#showError(this.dictMaxFilesExceededValue);
          done("Max files exceeded");
          return;
        }
        if (
          this.#isVideo(file) &&
          file.size > this.maxVideoFilesizeValue * 1024 * 1024
        ) {
          this.#showError(
            this.dictFileTooBigValue.replace(
              "{{maxFilesize}}",
              this.maxVideoFilesizeValue,
            ),
          );
          done("File too large");
          return;
        }
        done();
      },
    });
  }

  #previewTemplate() {
    return `
      <div class="comment-attachment-preview">
        <div class="comment-attachment-preview__thumb">
          <img data-dz-thumbnail class="comment-attachment-preview__img" />
          <div class="comment-attachment-preview__progress">
            <svg class="comment-attachment-preview__spinner" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" stroke-width="3" stroke="currentColor" stroke-dasharray="100" data-dz-uploadprogress />
            </svg>
          </div>
          <button type="button" class="comment-attachment-preview__remove" data-dz-remove aria-label="${this.dictRemoveFileValue}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18.3 5.7a1 1 0 00-1.4 0L12 10.6 7.1 5.7a1 1 0 00-1.4 1.4L10.6 12l-4.9 4.9a1 1 0 101.4 1.4L12 13.4l4.9 4.9a1 1 0 001.4-1.4L13.4 12l4.9-4.9a1 1 0 000-1.4z" fill="currentColor"/></svg>
          </button>
        </div>
        <span class="comment-attachment-preview__name label--small" data-dz-name></span>
      </div>
    `;
  }

  #loadExistingAttachments() {
    this.existingAttachmentsValue.forEach((file) => {
      const mockFile = {
        name: file.name,
        size: file.byte_size,
        type: file.content_type,
        accepted: true,
        attachment_id: file.attachment_id,
        isExisting: true,
      };
      this.dropzone.files.push(mockFile);
      this.dropzone.displayExistingFile(mockFile, file.url, null, "*", false);

      if (file.content_type === "application/pdf") {
        this.#renderPdfPreview(mockFile);
      } else if (!file.content_type.startsWith("image/")) {
        this.#renderDocumentPreview(mockFile);
      }
    });

    if (this.existingAttachmentsValue.length > 0) {
      this.previewsTarget.classList.add("has-files");
    }
  }

  #loadExistingVideos() {
    this.existingVideosValue.forEach((video) => {
      const mockFile = {
        name: video.name || "Video",
        size: 0,
        type: "video/mp4",
        accepted: true,
        video_id: video.id,
        isExisting: true,
      };
      this.dropzone.files.push(mockFile);
      this.dropzone.emit("addedfile", mockFile);
      this.dropzone.emit("complete", mockFile);
      mockFile.status = Dropzone.SUCCESS;

      this.#renderVideoPreview(mockFile);

      const progressEl = mockFile.previewElement?.querySelector(
        ".comment-attachment-preview__progress",
      );
      if (progressEl) progressEl.style.display = "none";

      if (video.thumbnail_url && mockFile.previewElement) {
        const thumb = mockFile.previewElement.querySelector(
          ".comment-attachment-preview__thumb",
        );
        if (thumb) {
          const videoIcon = thumb.querySelector(
            ".comment-attachment-preview__video-icon",
          );
          if (videoIcon) videoIcon.style.display = "none";

          const img = thumb.querySelector(".comment-attachment-preview__img");
          if (img) {
            this.#loadThumbnailWithRetry(img, video.thumbnail_url);
          }
        }
      }

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "comment[video_ids][]";
      input.value = video.id;
      mockFile.previewElement?.appendChild(input);
    });

    if (this.existingVideosValue.length > 0) {
      this.previewsTarget.classList.add("has-files");
    }
  }

  #renderVideoPreview(file) {
    if (!file.previewElement) return;
    const img = file.previewElement.querySelector("[data-dz-thumbnail]");
    if (img) {
      img.style.display = "none";
      const thumb = file.previewElement.querySelector(
        ".comment-attachment-preview__thumb",
      );
      if (thumb) {
        const videoIcon = document.createElement("div");
        videoIcon.className = "comment-attachment-preview__video-icon";
        videoIcon.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H16C17.1 20 18 19.1 18 18V14.5L22 18.5V5.5L18 9.5V6C18 4.9 17.1 4 16 4H4ZM4 6H16V18H4V6Z" fill="currentColor"/></svg>`;
        thumb.insertBefore(videoIcon, thumb.firstChild);
      }
    }

    // Replace default progress overlay with video upload status wrapper
    const progressEl = file.previewElement.querySelector(
      ".comment-attachment-preview__progress",
    );
    if (progressEl) {
      progressEl.innerHTML = `
        <div class="comment-attachment-preview__status-wrapper">
          <svg class="comment-attachment-preview__spinner" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="16" fill="none" stroke-width="3" stroke="currentColor" stroke-dasharray="100" data-dz-uploadprogress />
          </svg>
          <span class="comment-attachment-preview__status-label">${this.videoUploadingTextValue} 0%</span>
        </div>
      `;
    }
  }

  #renderPdfPreview(file) {
    if (!file.previewElement) return;
    const img = file.previewElement.querySelector("[data-dz-thumbnail]");
    if (img) img.style.display = "none";
    const thumb = file.previewElement.querySelector(
      ".comment-attachment-preview__thumb",
    );
    if (thumb) {
      const truncatedName = truncateFilename(file.name, 15);
      const fileSize = formatFileSize(file.size);
      const tile = document.createElement("div");
      tile.className = "comment-attachment-preview__pdf-tile";

      // SVG badge (safe, hardcoded template)
      const badgeDiv = document.createElement("div");
      badgeDiv.innerHTML = pdfBadgeSvg.replace("<svg", '<svg class="comment-attachment-preview__pdf-icon"');
      tile.appendChild(badgeDiv.firstChild);

      // Filename (escaped via textContent)
      const nameEl = document.createElement("span");
      nameEl.className = "comment-attachment-preview__pdf-name label--small";
      nameEl.textContent = truncatedName;
      tile.appendChild(nameEl);

      // File size and type (safe, controlled content)
      const metaEl = document.createElement("span");
      metaEl.className = "comment-attachment-preview__pdf-meta label--small";
      metaEl.textContent = `PDF • ${fileSize}`;
      tile.appendChild(metaEl);

      thumb.insertBefore(tile, thumb.firstChild);
    }
  }

  #renderDocumentPreview(file) {
    if (!file.previewElement) return;
    const img = file.previewElement.querySelector("[data-dz-thumbnail]");
    if (img) {
      img.style.display = "none";
      const thumb = file.previewElement.querySelector(
        ".comment-attachment-preview__thumb",
      );
      if (thumb) {
        const docIcon = document.createElement("div");
        docIcon.className = "comment-attachment-preview__doc-icon";
        docIcon.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 22C5.45 22 4.979 21.804 4.588 21.413C4.196 21.02 4 20.55 4 20V4C4 3.45 4.196 2.979 4.588 2.588C4.979 2.196 5.45 2 6 2H13.175C13.442 2 13.696 2.05 13.938 2.15C14.179 2.25 14.392 2.392 14.575 2.575L19.425 7.425C19.608 7.608 19.75 7.821 19.85 8.063C19.95 8.304 20 8.558 20 8.825V20C20 20.55 19.804 21.02 19.413 21.413C19.02 21.804 18.55 22 18 22H6Z" fill="currentColor"/></svg>`;
        thumb.insertBefore(docIcon, thumb.firstChild);
      }
    }
  }

  #handleRemoval(file) {
    if (file.isExisting && file.attachment_id) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "comment[attachment_deletions][]";
      input.value = file.attachment_id;
      this.element.appendChild(input);
    }

    if (file.isExisting && file.video_id) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "comment[video_deletions][]";
      input.value = file.video_id;
      this.element.appendChild(input);
    }

    // Abort in-progress tus upload
    const tusUpload = this.tusUploads.get(file);
    if (tusUpload) {
      tusUpload.abort();
      this.tusUploads.delete(file);
      this.uploadsInProgress--;
      if (this.uploadsInProgress === 0) {
        this.element.dispatchEvent(
          new CustomEvent("comment-attachment:upload-complete", {
            bubbles: true,
          }),
        );
      }
    }

    this.#emitChange();
  }

  #handleMaxExceeded(file) {
    this.dropzone.removeFile(file);
    this.#showError(
      this.dictMaxFilesExceededValue.replace(
        "{{maxFiles}}",
        this.maxFilesValue,
      ),
    );
  }

  #showError(message) {
    let errorEl = this.previewsTarget.querySelector(
      ".comment-attachment-error",
    );
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.className = "comment-attachment-error label--small";
      this.previewsTarget.appendChild(errorEl);
    }
    errorEl.textContent = message;
    setTimeout(() => errorEl.remove(), 4000);
  }

  #emitChange() {
    const hasAny = this.previewsTarget.querySelectorAll(".comment-attachment-preview").length > 0;
    this.previewsTarget.classList.toggle("has-files", hasAny);
    this.element.dispatchEvent(
      new CustomEvent("comment-attachment:change", {
        bubbles: true,
        detail: { hasFiles: hasAny },
      }),
    );
  }

  #preventSubmitDuringUpload(event) {
    if (this.uploadsInProgress > 0) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  async #upload(file) {
    if (this.#isVideo(file)) {
      return this.#uploadVideo(file);
    }
    return this.#uploadAttachment(file);
  }

  async #uploadAttachment(file) {
    this.uploadsInProgress++;
    this.element.dispatchEvent(
      new CustomEvent("comment-attachment:uploading", { bubbles: true }),
    );

    try {
      await refreshCsrfToken();
    } catch {
      this.uploadsInProgress--;
      this.#uploadError(file, this.uploadErrorValue);
      if (this.uploadsInProgress === 0) {
        this.element.dispatchEvent(
          new CustomEvent("comment-attachment:upload-complete", {
            bubbles: true,
          }),
        );
      }
      return;
    }

    if (file.type?.startsWith("image/")) {
      file._imageDimensions = await this.#getImageDimensions(file);
    }

    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            this.#updateProgress(file, progress);
          }
        });
        file.status = Dropzone.PROCESSING;
        this.dropzone.emit("processing", file);
      },
    });

    upload.create((error, blob) => {
      this.uploadsInProgress--;

      if (error) {
        this.#uploadError(file, error);
      } else {
        this.#attachmentUploadSuccess(file, blob);
      }

      if (this.uploadsInProgress === 0) {
        this.element.dispatchEvent(
          new CustomEvent("comment-attachment:upload-complete", {
            bubbles: true,
          }),
        );
      }
    });
  }

  async #uploadVideo(file) {
    this.uploadsInProgress++;
    this.element.dispatchEvent(
      new CustomEvent("comment-attachment:uploading", { bubbles: true }),
    );

    file.status = Dropzone.PROCESSING;
    this.dropzone.emit("processing", file);

    try {
      await refreshCsrfToken();

      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]',
      )?.content;
      const initResponse = await fetch(this.videoInitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ filename: file.name, ...(await this.#getVideoDimensions(file)) }),
      });

      if (!initResponse.ok) {
        throw new Error("Failed to initialize video upload");
      }

      const data = await initResponse.json();
      file.video_id = data.db_id;

      const thumbnailTime = 1000;

      const tusUpload = new tus.Upload(file, {
        endpoint: data.endpoint,
        headers: {
          AuthorizationSignature: data.auth_signature,
          AuthorizationExpire: data.auth_expire,
          VideoId: data.video_id,
          LibraryId: data.library_id,
        },
        metadata: {
          filetype: file.type,
          title: file.name,
          ...(thumbnailTime !== undefined && {
            thumbnailTime: String(thumbnailTime),
          }),
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const progress = (bytesUploaded / bytesTotal) * 100;
          this.#updateProgress(file, progress);
        },
        onSuccess: () => {
          this.tusUploads.delete(file);
          const thumbnailUrl = `https://${data.library_hostname}/${data.video_id}/thumbnail.jpg`;
          this.#videoUploadComplete(file, data.db_id, thumbnailUrl);
        },

        onError: (error) => {
          this.tusUploads.delete(file);
          this.uploadsInProgress--;
          this.#uploadError(file, error.message || this.uploadErrorValue);

          if (this.uploadsInProgress === 0) {
            this.element.dispatchEvent(
              new CustomEvent("comment-attachment:upload-complete", {
                bubbles: true,
              }),
            );
          }
        },
      });

      this.tusUploads.set(file, tusUpload);
      tusUpload.start();
    } catch (error) {
      this.uploadsInProgress--;
      this.#uploadError(file, error.message || this.uploadErrorValue);

      if (this.uploadsInProgress === 0) {
        this.element.dispatchEvent(
          new CustomEvent("comment-attachment:upload-complete", {
            bubbles: true,
          }),
        );
      }
    }
  }

  #updateProgress(file, progress) {
    if (!file.previewElement) return;
    const circle = file.previewElement.querySelector(
      "[data-dz-uploadprogress]",
    );
    if (circle) {
      const circumference = 2 * Math.PI * 16;
      const offset = circumference - (progress / 100) * circumference;
      circle.style.strokeDasharray = `${circumference}`;
      circle.style.strokeDashoffset = `${offset}`;
    }

    // Update percentage text for video uploads
    if (this.#isVideo(file)) {
      const label = file.previewElement.querySelector(
        ".comment-attachment-preview__status-label",
      );
      if (label) {
        label.textContent = `${this.videoUploadingTextValue} ${Math.round(progress)}%`;
      }
    }
  }

  #attachmentUploadSuccess(file, blob) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.paramNameValue;
    input.value = blob.signed_id;
    file.previewElement?.appendChild(input);

    const dims = file._imageDimensions;
    if (dims?.width && dims?.height) {
      for (const dim of ["width", "height"]) {
        const dimInput = document.createElement("input");
        dimInput.type = "hidden";
        dimInput.name = `attachment_dimensions[${blob.signed_id}][${dim}]`;
        dimInput.value = dims[dim];
        file.previewElement?.appendChild(dimInput);
      }
    }

    file.status = Dropzone.SUCCESS;
    this.dropzone.emit("success", file);
    this.dropzone.emit("complete", file);

    const progressEl = file.previewElement?.querySelector(
      ".comment-attachment-preview__progress",
    );
    if (progressEl) progressEl.style.display = "none";
  }

  #videoUploadComplete(file, dbId, thumbnailUrl) {
    this.uploadsInProgress--;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "comment[video_ids][]";
    input.value = dbId;
    file.previewElement?.appendChild(input);

    file.status = Dropzone.SUCCESS;
    this.dropzone.emit("success", file);
    this.dropzone.emit("complete", file);

    const progressEl = file.previewElement?.querySelector(
      ".comment-attachment-preview__progress",
    );
    if (progressEl) progressEl.style.display = "none";

    if (thumbnailUrl && file.previewElement) {
      const thumb = file.previewElement.querySelector(
        ".comment-attachment-preview__thumb",
      );
      if (thumb) {
        const videoIcon = thumb.querySelector(
          ".comment-attachment-preview__video-icon",
        );
        if (videoIcon) videoIcon.style.display = "none";

        const img = thumb.querySelector(".comment-attachment-preview__img");
        if (img) {
          this.#loadThumbnailWithRetry(img, thumbnailUrl);
        }
      }
    }

    if (this.uploadsInProgress === 0) {
      this.element.dispatchEvent(
        new CustomEvent("comment-attachment:upload-complete", {
          bubbles: true,
        }),
      );
    }
  }

  #getImageDimensions(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      img.src = url;
    });
  }

  #getVideoDimensions(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      video.src = url;
    });
  }

  #loadThumbnailWithRetry(img, url, attempt = 0) {
    const maxAttempts = 10;
    const delay = 3000;

    // Show spinner on first attempt
    const thumb = img.closest(".comment-attachment-preview__thumb");
    let spinner = thumb?.querySelector(
      ".comment-attachment-preview__thumb-spinner",
    );
    if (!spinner && thumb) {
      spinner = document.createElement("div");
      spinner.className = "comment-attachment-preview__thumb-spinner";
      thumb.appendChild(spinner);
    }

    img.onerror = () => {
      if (attempt < maxAttempts) {
        setTimeout(
          () => this.#loadThumbnailWithRetry(img, url, attempt + 1),
          delay,
        );
      } else {
        spinner?.remove();
      }
    };
    img.onload = () => {
      img.onerror = null;
      img.style.display = "";
      spinner?.remove();
    };
    const separator = url.includes("?") ? "&" : "?";
    img.src = `${url}${separator}_retry=${attempt}`;
  }

  #uploadError(file, error) {
    file.status = Dropzone.ERROR;
    this.dropzone.emit("error", file, error);
    this.dropzone.emit("complete", file);

    if (file.previewElement) {
      file.previewElement.classList.add("comment-attachment-preview--error");
    }
  }
}
